import React, { useEffect, useMemo, useState } from 'react';
import SettingContainer from '../SettingContainer';
import ActivityLoader from '../../Common/ActivityLoader';
import Notification from '../../Common/Notification';
import { useTheme } from '../../../DataStore/ThemeContext';
import { useConfig } from '../../../DataStore/ConfigContext';
import { Skeleton, SkeletonPill } from '../../../util/skeleton/Skeleton';

/* ---------- Helpers ---------- */
const accountLabel = (acc) =>
  acc ? `${acc.account_name} — ${acc.account_number}` : 'Not set';

const SpecialAccountCard = ({ cfg, active, onSelect, onConfigure }) => {
  const isSet = !!cfg.account;
  
  return (
    <div
      className={`permission-card ${active ? 'selected' : ''}`}
      onClick={() => onSelect(cfg)}
      style={{ minHeight: '160px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      {/* Top Section: Flex-wrap allows the chip to drop below the title if space is tight */}
      <div className="d-flex justify-content-between align-items-start w-100 flex-wrap" style={{ gap: '8px' }}>
        <div className="d-flex flex-grow-1 mr-2" style={{ minWidth: '0' }}> {/* minWidth: 0 is crucial for text truncation */}
            <div className="icon-box mr-3 flex-shrink-0" style={{ 
                width: 42, height: 42, borderRadius: '10px', 
                background: '#F1F5F9', color: '#475569',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
                <i className="fa-solid fa-gears" />
            </div>
            <div style={{ minWidth: '0' }}>
                <span className="text-truncate" style={{ 
                    display: 'block', fontWeight: '700', fontSize: '14px', 
                    color: active ? '#4F46E5' : '#1E293B', textTransform: 'uppercase' 
                }}>
                    {cfg.designation}
                </span>
                <small className="text-muted d-block mt-1 text-truncate">
                    {accountLabel(cfg.account)}
                </small>
            </div>
        </div>
        
        {/* Chip Section: Wrapped in a div to prevent it from stretching vertically */}
        <div className="flex-shrink-0">
            <StatusChip isSet={isSet} />
        </div>
      </div>

      {/* Footer Section: Using flex-wrap for buttons on mobile */}
      <div className="mt-auto pt-3 border-top d-flex justify-content-between align-items-center flex-wrap" style={{ gap: '10px' }}>
          <button
            type="button"
            className="btn btn-modern-outline py-1 px-3"
            style={{ fontSize: '12px' }}
            onClick={(e) => {
              e.stopPropagation();
              onConfigure(cfg);
            }}
          >
            <i className="fa-solid fa-sliders mr-1" /> Configure
          </button>
  
          {isSet && (
            <div className="d-flex align-items-center text-indigo ml-auto" style={{ fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap' }}>
              <i className="fa-solid fa-circle-check mr-1" /> Active Mapping
            </div>
          )}
      </div>
    </div>
  );
};

/* ---------- Status Chip Refinement ---------- */
const StatusChip = ({ isSet }) => (
  <span style={{
    fontSize: '10px',
    fontWeight: '800',
    padding: '4px 10px',
    borderRadius: '6px',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    backgroundColor: isSet ? '#DCFCE7' : '#FEF9C3',
    color: isSet ? '#166534' : '#854d0e'
  }}>
    {isSet ? 'Configured' : 'Missing'}
  </span>
);

const Toolbar = ({ query, setQuery, onRefresh }) => (
  <div className="mb-4 mt-2 px-1 d-flex justify-content-between">
    <div className="input-group" style={{ 
        borderRadius: '10px', overflow: 'hidden', border: '1px solid #E2E8F0',
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)', maxWidth: '400px'
    }}>
      <span className="input-group-text bg-white border-0 text-muted">
        <i className="fa fa-search"></i>
      </span>
      <input
        type="text"
        className="form-control border-0 shadow-none"
        style={{ padding: '10px 10px', fontSize: '14px' }}
        placeholder="Search special accounts..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
    </div>

    <button className="btn btn-modern-outline" onClick={onRefresh}>
        <i className="fa-solid fa-rotate mr-1" /> Refresh
    </button>
  </div>
);

/* ---------- Skeleton while loading ---------- */
const CardsSkeleton = () => (
  <div className="row">
    {Array.from({ length: 6 }).map((_, i) => (
      <div className="col-xl-4 col-lg-6 col-md-6 mb-3" key={i}>
        <div className="card shadow-sm border-0">
          <div className="card-body">
            <div className="d-flex align-items-center">
              <span className="rounded d-inline-flex align-items-center justify-content-center mr-3" style={{ width: 44, height: 44, background: '#EEF2F6' }}>
                <i className="fa fa-cog text-muted" />
              </span>
              <div style={{ width: '70%' }}>
                <Skeleton w="80%" h={14} />
                <Skeleton w="60%" h={12} style={{ marginTop: 6 }} />
              </div>
            </div>
            <div className="mt-3">
              <SkeletonPill w={120} h={26} />
            </div>
            <div className="mt-3 d-flex justify-content-between">
              <Skeleton w="110px" h={28} />
              <Skeleton w="90px" h={12} />
            </div>
          </div>
        </div>
      </div>
    ))}
  </div>
);

/* ---------- Main Component ---------- */
const SpecialAccountsConfig = () => {
  const { openSettings } = useTheme();
  const [activeRecord, setActiveRecord] = useState(null);
  const [query, setQuery] = useState('');

  const {
    specialAccounts,
    getSpecialAccounts,
    loadingSpecialAccounts,
    errorSpecialAccounts,
  } = useConfig();

  useEffect(() => { getSpecialAccounts(); }, []); // eslint-disable-line

  const handleRefresh = () => getSpecialAccounts(true);
  const handleConfig = (field) =>
    openSettings('Special Account Configuration', 'ConfigSpecialAccount', field, "Save Configuration");

  const filtered = useMemo(() => {
    const list = Array.isArray(specialAccounts) ? specialAccounts : [];
    const q = query.trim().toLowerCase();
    return q
      ? list.filter(
          (c) =>
            c.designation?.toLowerCase().includes(q) ||
            accountLabel(c.account).toLowerCase().includes(q)
        )
      : list;
  }, [specialAccounts, query]);

  const menu = () => (
    <>
      <button type="button" tabIndex="0" className="dropdown-item" onClick={handleRefresh}>
        <i className="dropdown-icon fa fa-sync" /> <span>Refresh</span>
      </button>
    </>
  );

  return (
    <SettingContainer title="Special Accounts" icon="fa fa-bank" Menu={menu}>
      <Toolbar query={query} setQuery={setQuery} onRefresh={handleRefresh} />

      {/* States */}
      {errorSpecialAccounts && <Notification type="danger" message={String(errorSpecialAccounts)} />}

      {loadingSpecialAccounts && !specialAccounts ? (
        <CardsSkeleton />
      ) : specialAccounts === '404' ? (
        <div className="text-center p-5 border rounded">
          <div className="mb-2">
            <span className="rounded-circle d-inline-flex align-items-center justify-content-center bg-light" style={{ width: 64, height: 64 }}>
              <i className="fa fa-wallet text-muted" />
            </span>
          </div>
          <div className="h6">No special accounts found</div>
          <div className="text-muted">Configure mapping to accounts used for special cases (e.g., rounding differences, retained earnings, suspense).</div>
          <button className="btn btn-primary btn-sm mt-2" onClick={handleRefresh}>
            <i className="fa fa-sync mr-1" />
            Try Again
          </button>
        </div>
      ) : Array.isArray(filtered) && filtered.length > 0 ? (
        <div className="row">
          {filtered.map((config) => (
            <div className="col-xl-4 col-lg-6 col-md-6 mb-3" key={config.id}>
              <SpecialAccountCard
                cfg={config}
                active={activeRecord?.id === config.id}
                onSelect={setActiveRecord}
                onConfigure={handleConfig}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center text-muted p-4">No results for “{query}”.</div>
      )}

      {/* Fallback loader if needed */}
      {!specialAccounts && !errorSpecialAccounts && !loadingSpecialAccounts && (
        <div className="py-4 text-center"><ActivityLoader /></div>
      )}
    </SettingContainer>
  );
};

export default SpecialAccountsConfig;
